import React, { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import { EN } from "../i18n/serviceContentEN";
import { AR } from "../i18n/serviceContentAR";
import { buildLayout, variantFor } from "../components/service/serviceLayout";
import { Overview, Closing } from "../components/service/Overview";
import { Offerings } from "../components/service/Offerings";
import { Steps } from "../components/service/Steps";
import { Checklist } from "../components/service/Checklist";
import { container, item, inView } from "../components/service/presets";
import { ArrowRight } from "lucide-react";

const CYBER_URL = "https://cyberquess.com/";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, dir, lang } = useLang();
  const navigate = useNavigate();
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";

  const idx = t.servicesList.findIndex((s) => s.slug === slug);
  const service = idx >= 0 ? t.servicesList[idx] : null;
  const content = (lang === "ar" ? AR : EN)[slug] || null;

  const layout = useMemo(() => (content ? buildLayout(content.blocks) : null), [content]);
  const variant = useMemo(() => variantFor(idx < 0 ? 0 : idx), [idx]);

  useEffect(() => {
    if (slug === "cyber-security") {
      window.location.replace(CYBER_URL);
      return;
    }
    if (!service) navigate("/services");
  }, [service, slug, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (slug === "cyber-security" || !service || !content || !layout) return null;

  const others = t.servicesList.filter((s) => s.slug !== slug && s.slug !== "cyber-security").slice(0, 4);
  const capsLabel = lang === "ar" ? "أبرز القدرات" : "Key Capabilities";

  return (
    <div>
      <PageHero tag={t.servicesSection.tag} title={content.title} desc={content.intro} />

      <Overview icon={service.icon} pre={layout.pre} capsLabel={capsLabel} features={service.features} service={service} />

      {layout.sections.map((sec, i) => {
        const dark = i % 2 === 0;
        if (sec.type === "offerings")
          return <Offerings key={i} variant={variant.offerings} heading={sec.heading} groups={sec.groups} dark={dark} dir={dir} />;
        if (sec.type === "checklist")
          return <Checklist key={i} variant={variant.checklist} group={sec.group} dark={dark} dir={dir} />;
        if (sec.type === "steps")
          return <Steps key={i} variant={variant.steps} group={sec.group} dark={dark} dir={dir} />;
        if (sec.type === "closing") return <Closing key={i} group={sec.group} dark={dark} />;
        return null;
      })}

      {/* CTA band */}
      <section className="rq-dark py-16 md:py-20 relative overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[360px] w-[360px] rounded-full blur-[120px] opacity-30" style={{ background: "radial-gradient(circle,#14a24b,transparent 70%)" }} />
        <div className="rq-container relative z-10">
          <motion.div {...inView} variants={container} className="max-w-2xl mx-auto text-center">
            <motion.h2 variants={item} className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
              {lang === "ar" ? `هل أنت مستعد للبدء مع ${service.title}؟` : `Ready to get started with ${service.title}?`}
            </motion.h2>
            <motion.p variants={item} className="mt-5 text-[15px] md:text-lg text-[color:var(--rq-muted)] leading-relaxed">
              {lang === "ar"
                ? "دع خبراءنا يصمّمون حلًّا يناسب أهداف مؤسستك. احجز استشارتك المجانية اليوم."
                : "Let our experts craft a solution tailored to your goals. Book your free consultation today."}
            </motion.p>
            <motion.div variants={item} className="mt-8 flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="rq-glow-btn inline-flex items-center gap-2 px-8 py-4" data-testid="service-detail-contact-cta">
                {lang === "ar" ? "تواصل مع فريقنا" : "Talk to Our Team"}
                <ArrowRight size={18} style={{ transform: flip }} />
              </Link>
              <Link to="/services" className="rq-ghost-btn inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold">
                {lang === "ar" ? "كل الخدمات" : "All Services"}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Related services */}
      <section className="py-14 rq-light">
        <div className="rq-container">
          <h3 className="font-display text-xl font-semibold text-white mb-6">
            {lang === "ar" ? "خدمات أخرى" : "Explore Other Services"}
          </h3>
          <div className="flex flex-wrap gap-3">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="rq-ghost-btn px-4 py-2 text-sm inline-flex items-center gap-2" data-testid={`related-service-${s.slug}`}>
                {s.title}
                <ArrowRight size={14} style={{ transform: flip }} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
